import { AgDialog } from '@australiangreens/ag-internal-components';
import { PropsWithChildren, useRef, useState } from 'react';

import { Context, ContextType } from './Context';

/**
 * This component is used to guard against unsaved changes in the Rocket contact
 * form. It displays a dialog warning the user when the form has unsaved
 * changes.
 *
 * It does not detect navigation automatically, that needs to be done by calling
 * the function returned by the useCheckRocketFormStatus() hook.
 */
export function UnsavedRocketChangesGuardProvider({ children }: PropsWithChildren) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const resultResolverRef = useRef<((result: boolean) => void) | undefined>(undefined);

  const value: ContextType = {
    isDialogOpen,
    setIsDialogOpen,
    resultResolverRef,
    isEnabled,
    setIsEnabled,
  };

  const handleClickOk = async () => {
    setIsDialogOpen(false);
    if (resultResolverRef.current) resultResolverRef.current(false);
  };

  const handleClickProceedAnyway = async () => {
    setIsDialogOpen(false);
    if (resultResolverRef.current) resultResolverRef.current(true);
  };

  return (
    <Context.Provider value={value}>
      {children}

      <AgDialog
        dialogTitle="Unsaved changes in Rocket"
        isOpen={isDialogOpen}
        primaryButton={{ text: 'OK', onClick: handleClickOk }}
        secondaryButton={{
          text: 'Proceed anyway',
          onClick: handleClickProceedAnyway,
          buttonColor: 'error',
        }}
        onClose={() => {}}
        disableCloseOnBackdropOrEscape
      >
        <p>
          You have unsaved changes in the Rocket contact form. Please save them, or click the cancel
          button in Rocket before continuing.
        </p>
      </AgDialog>
    </Context.Provider>
  );
}
